
import React from "react";
import { Button } from "@/components/ui/button";
import { ProjectFilter } from "@/components/common/ProjectFilter";
import { DateRangePicker, DateRange } from "@/components/common/DateRangePicker";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RefreshCw, X } from "lucide-react";

interface FilterBarProps {
  selectedProjectId: string | "all";
  projects: Array<{
    id: string;
    name: string;
    isTeamProject?: boolean;
  }>;
  dateRange: DateRange;
  showBillableOnly?: boolean;
  showTeamProjectsOnly?: boolean;
  onProjectChange: (projectId: string | "all") => void;
  onDateRangeChange: (range: DateRange) => void;
  onBillableToggle?: (enabled: boolean) => void;
  onTeamProjectsToggle?: (enabled: boolean) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  hasActiveFilters: boolean;
}

export function FilterBar({
  selectedProjectId,
  projects,
  dateRange,
  showBillableOnly = false,
  showTeamProjectsOnly = false,
  onProjectChange,
  onDateRangeChange,
  onBillableToggle,
  onTeamProjectsToggle,
  onClearFilters,
  onApplyFilters,
  hasActiveFilters
}: FilterBarProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/20 rounded-lg border">
      <div className="flex flex-col sm:flex-row gap-3 flex-1">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Project</label>
          <ProjectFilter
            selectedProjectId={selectedProjectId}
            projects={projects as any}
            onProjectChange={onProjectChange}
            placeholder="All Projects"
          />
        </div>
        
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Date Range</label>
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
          />
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-muted-foreground">Filters</label>
          <div className="space-y-2">
            {onBillableToggle && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="billable-only"
                  checked={showBillableOnly}
                  onCheckedChange={onBillableToggle}
                />
                <Label htmlFor="billable-only" className="text-sm">
                  Billable only
                </Label>
              </div>
            )}
            
            {onTeamProjectsToggle && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="team-projects-only"
                  checked={showTeamProjectsOnly}
                  onCheckedChange={onTeamProjectsToggle}
                />
                <Label htmlFor="team-projects-only" className="text-sm">
                  Team projects
                </Label>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex gap-2 items-end">
        <Button onClick={onApplyFilters} className="h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Apply
        </Button>
        {hasActiveFilters && (
          <Button variant="outline" onClick={onClearFilters} className="h-10">
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
